import { Injectable } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
@Injectable({
  providedIn: "root",
})
// Using this as a shared service for getting copies of various forms. Might help not having to recreate the form every time we need one on a different page.
// Might be able to define like a "Base" form model for each model and then make modifications to it on the functions to return like an edit version or a new version.
export class FormRepoService {
  constructor(private formBuilder: FormBuilder) {}

  //#region ACCOUNT FORMS **************************************************

  newUserAccountForm(): FormGroup {
    return this.formBuilder.group({
      accountName: ["", [Validators.required]],
      firstName: ["", [Validators.required]],
      lastName: ["", [Validators.required]],
      suffix: [""],
      jobTitle: [""],
      country: ["US", [Validators.required]],
      internationalAddress: [""],
      address: ["", []],
      city: ["", []],
      state: ["", []],
      zip: ["", []],
      phone: ["", [Validators.required, Validators.pattern("^[- +()0-9]+$")]],
      email: ["", [Validators.required, Validators.email]],
      password: [
        "",
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&]).*$/
          ),
        ],
      ],
      confirmPassword: ["", [Validators.required]],
    });
  }

  newAccountForm(): FormGroup {
    return this.formBuilder.group({
      name: ["", [Validators.required]],
      ownerName: [""],
      accountNumber: ["", []],
      country: ["US", [Validators.required]],
      internationalAddress: [""],
      address: ["", []],
      city: ["", []],
      state: ["", []],
      zip: ["", []],
      accountPhone: [
        "",
        [Validators.required, Validators.pattern("^[- +()0-9]+$")],
      ],
      accountEmail: ["", [Validators.required, Validators.email]],
      isActive: ["true", [Validators.required]],
      autoSubmitTimesheets: ["false", [Validators.required]],
      modifyApprovedTimesheets: ["false", [Validators.required]],
      autoApproveTimesheets: ["false", [Validators.required]],
      enableShiftAlerts: ["true", [Validators.required]],
      managerShiftAlert: [
        "2",
        [Validators.required, Validators.pattern("^[0-9]+$")],
      ],
      staffShiftAlert: [
        "12",
        [Validators.required, Validators.pattern("^[0-9]+$")],
      ],
      shiftAlertToEmail: [""],
      enableClaimShift: ["true", [Validators.required]],
      enableCredentials: ["true", [Validators.required]],
      enableClientContacts: ["true", [Validators.required]],
      enableTimesheets: ["true", [Validators.required]],
      enableRequestOff: ["true", [Validators.required]],
      enableSwapRequest: ["true", [Validators.required]],
      enablePayBillRate: ["true", [Validators.required]],
      enableNewShift: ["true", [Validators.required]],
      employeeTag: ["Provider", [Validators.required]],
      clientTag: ["Client", [Validators.required]],
    });
  }

  //#endregion

  //#region USER FORMS **************************************************

  newUsersForm(): FormGroup {
    return this.formBuilder.group({
      firstName: ["", [Validators.required]],
      lastName: ["", [Validators.required]],
      jobTitle: [""],
      suffix: [""],
      country: ["US", [Validators.required]],
      internationalAddress: [""],
      address: ["", []],
      city: ["", []],
      state: ["", []],
      zip: ["", []],
      phone: ["", [Validators.required, Validators.pattern("^[- +()0-9]+$")]],
      email: ["", [Validators.required, Validators.email]],
      password: [
        "",
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&]).*$/
          ),
        ],
      ],
      //roleId: ["", [Validators.required]],
      isActive: [true, [Validators.required]],
      confirmPassword: ["", [Validators.required]],
      maxHoursPerWeek: [null],
    });
  }

  editUsersForm(): FormGroup {
    return this.formBuilder.group({
      firstName: ["", [Validators.required]],
      lastName: ["", [Validators.required]],
      suffix: ["", []],
      jobTitle: [""],
      country: ["US", [Validators.required]],
      internationalAddress: [""],
      address: [""],
      city: [""],
      state: [""],
      zip: [""],
      phone: ["", [Validators.required, Validators.pattern("^[- +()0-9]+$")]],
      email: ["", [Validators.required, Validators.email]],
      //roleId: ["", [Validators.required]],
      isActive: [true, [Validators.required]],
      maxHoursPerWeek: [null],
    });
  }

  //#endregion

  //#region PROVIDER FORMS **************************************************

  newProviderCredentialForm(): FormGroup {
    return this.formBuilder.group({
      expirationDate: ["", [Validators.required]],
      name: ["", [Validators.required]],
    });
  }

  editProviderCredentialForm(): FormGroup {
    return this.formBuilder.group({
      expirationDate: ["", [Validators.required]],
      name: ["", [Validators.required]],
    });
  }

  newProviderFacilityForm(): FormGroup {
    return this.formBuilder.group({
      clientFacilityJobCodeId: [null, [Validators.required]],
      payRate: [null],
      billRate: [],
      shiftNotificationDelay: [0, [Validators.required]],
      defaultMileage: [0, [Validators.required]],
    });
  }

  editProviderFacilityForm(): FormGroup {
    return this.formBuilder.group({
      clientFacilityJobCodeId: [null, [Validators.required]],
      payRate: [null],
      billRate: [],
      shiftNotificationDelay: [0, [Validators.required]],
      defaultMileage: [0, [Validators.required]],
    });
  }

  newTimeAvailability(): FormGroup {
    return this.formBuilder.group({
      startTime: [null, [Validators.required]],
      duration: [null, [Validators.required]],
      dayOfWeek: [0, [Validators.required]],
    });
  }

  //#endregion

  //#region CLIENT FORMS **************************************************

  newClientForm(): FormGroup {
    return this.formBuilder.group({
      name: ["", [Validators.required]],
      address: [""],
      country: ["US", [Validators.required]],
      internationalAddress: [""],
      city: [""],
      state: [""],
      zip: [""],
      notes: ["", []],
    });
  }

  editClientForm(): FormGroup {
    return this.formBuilder.group({
      name: ["", [Validators.required]],
      address: [""],
      country: ["US", [Validators.required]],
      internationalAddress: [""],
      city: [""],
      state: [""],
      zip: [""],
      notes: ["", []],
    });
  }

  //#endregion

  //#region CLIENT CONTACT FORMS **************************************************

  newClientContactForm(): FormGroup {
    return this.formBuilder.group({
      name: ["", [Validators.required]],
      phone: ["", [Validators.pattern("^[- +()0-9]+$")]],
      email: ["", [Validators.email]],
      title: [""],
    });
  }

  editClientContactForm(): FormGroup {
    return this.formBuilder.group({
      name: ["", [Validators.required]],
      phone: ["", [Validators.pattern("^[- +()0-9]+$")]],
      email: ["", [Validators.email]],
      title: [""],
    });
  }

  //#endregion

  //#region CLIENT FACILITY FORMS ************************

  newClientFacilityForm(): FormGroup {
    return this.formBuilder.group({
      clientId: ["", [Validators.required]],
      name: ["", [Validators.required]],
      address: [""],
      country: ["US", [Validators.required]],
      internationalAddress: [""],
      city: [""],
      state: [""],
      zip: [""],
      facilityTypeId: ["", [Validators.required]],
    });
  }

  editClientFacilityForm(): FormGroup {
    return this.formBuilder.group({
      name: ["", [Validators.required]],
      clientId: [""],
      address: [""],
      country: ["US", [Validators.required]],
      internationalAddress: [""],
      city: [""],
      state: [""],
      zip: [""],
      facilityTypeId: ["", [Validators.required]],
    });
  }

  newFacilityTypeForm(): FormGroup {
    return this.formBuilder.group({
      facilityType: ["", [Validators.required]],
    });
  }

  editFacilityTypeForm(): FormGroup {
    return this.formBuilder.group({
      facilityType: ["", [Validators.required]],
    });
  }

  //#endregion

  //#region CLIENT JOB CODE FORMS ************************

  newClientFacilityJobCodeForm(): FormGroup {
    return this.formBuilder.group({
      clientFacilityId: [""],
      clientFacilityJobCodeDescription: ["", [Validators.required]],
      payRate: ["", [Validators.required]],
      billRate: ["", [Validators.required]],
    });
  }

  editClientFacilityJobCodeForm(): FormGroup {
    return this.formBuilder.group({
      clientFacilityJobCodeDescription: ["", [Validators.required]],
      payRate: ["", [Validators.required]],
      billRate: ["", [Validators.required]],
    });
  }

  //#endregion

  //#region SHIFT FORMS ************************

  shiftSearchForm(): FormGroup {
    return this.formBuilder.group({
      clientFacilityId: [""],
      clientFacilityJobCodeId: [""],
      providerId: [""],
      shiftStatusTypeId: ["-1"],
      startDate: [""],
      endDate: [""],
    });
  }

  newShiftForm(): FormGroup {
    return this.formBuilder.group({
      userId: [null],
      clientFacilityJobCodeId: ["", [Validators.required]],
      recurrenceTypeId: [1, [Validators.required]],
      startDate: [null, [Validators.required]],
      hours: [null, [Validators.required]],
      bonusRate: [1],
      recurrenceInterval: [1],
      recurrenceEndDate: [null],
    });
  }

  copyShiftForm(): FormGroup {
    return this.formBuilder.group({
      clientFacilityJobCodeId: [""],
      recurrenceTypeId: [1, [Validators.required]],
      startDate: [null, [Validators.required]],
      duration: [null, [Validators.required]],
      copyAssigned: [false],
    });
  }

  editShiftForm(): FormGroup {
    return this.formBuilder.group({
      userId: [""],
      clientFacilityJobCodeId: ["", [Validators.required]],
      recurrenceTypeId: [1, [Validators.required]],
      startDate: [null, [Validators.required]],
      hours: [null, [Validators.required]],
      bonusRate: [0],
      recurrenceInterval: [1],
      recurrenceEndDate: [null],
      updateSeries: [false],
    });
  }

  //#endregion

  //#region TIMESHEET FORMS ************************

  timesheetSearchForm(): FormGroup {
    return this.formBuilder.group({
      clientFacilityId: [""],
      providerId: [""],
      shiftStatusTypeId: [""],
      timesheetStatusTypeId: [""],
      startDate: [""],
      endDate: [""],
    });
  }

  newTimesheetForm(): FormGroup {
    return this.formBuilder.group({
      userId: ["", [Validators.required]],
      clientFacilityJobCodeId: ["", [Validators.required]],
      startDate: ["", [Validators.required]],
      hours: ["", [Validators.required]],
      payRate: [""],
      billRate: [""],
      mileage: [null],
      notes: [null],
    });
  }

  editTimesheetForm(): FormGroup {
    return this.formBuilder.group({
      userId: [""],
      clientFacilityJobCodeId: [""],
      startDate: ["", [Validators.required]],
      hours: ["", [Validators.required]],
      timesheetStatusTypeId: [""],
      payRate: ["", [Validators.required]],
      billRate: ["", [Validators.required]],
      mileage: [""],
      notes: [""],
    });
  }

  newTimesheetExpenseForm(): FormGroup {
    return this.formBuilder.group({
      timesheetId: [""],
      timesheetExpenseTypeId: ["", [Validators.required]],
      cost: ["", [Validators.required]],
      description: [""],
    });
  }

  editTimesheetExpenseForm(): FormGroup {
    return this.formBuilder.group({
      timesheetId: [""],
      timesheetExpenseTypeId: ["", [Validators.required]],
      cost: ["", [Validators.required]],
      description: [""],
    });
  }

  //#endregion

  //#region TIMESHEET FORMS ************************

  newNotificationForm(): FormGroup {
    return this.formBuilder.group({
      userId: ["", [Validators.required]],
      message: [""],
    });
  }

  //#endregion

  exportSearchForm(): FormGroup {
    return this.formBuilder.group({
      weekEndDate: [""],
      userId: ["showAllProviders"],
    });
  }

  timeoffRequestSearchForm(): FormGroup {
    return this.formBuilder.group({
      providerId: [""],
      requestStartDate: [""],
      endDate: [""],
      status: [0],
    });
  }

  newTimeOffRequestForm(): FormGroup {
    return this.formBuilder.group({
      userId: ["", [Validators.required]],
      allDay: [true],
      requestStartDate: ["", [Validators.required]],
      duration: ["24", [Validators.required]],
    });
  }

  updateTimeOffRequestForm(): FormGroup {
    return this.formBuilder.group({
      userId: ["", [Validators.required]],
      requestStartDate: ["", [Validators.required]],
      duration: ["", [Validators.required]],
      status: ["", [Validators.required]],
    });
  }

  //#endregion

  //#region SCHEDULER ************************

  weekForm(): FormGroup {
    return this.formBuilder.group({
      weekStart: ["", [Validators.required]],
    });
  }

  assignProvider(): FormGroup {
    return this.formBuilder.group({
      userId: [" ", [Validators.required]],
    });
  }

  newScheduleForm(): FormGroup {
    return this.formBuilder.group({
      clientFacilityId: ["", [Validators.required]],
      dateStart: ["", [Validators.required]],
    });
  }
  //#endregion

  //#region CALL OFF FORMS **************************************************
  newCallOffForm(): FormGroup {
    return this.formBuilder.group({
      reason: ["", [Validators.required]],
    });
  }
  callOffSearchForm(): FormGroup {
    return this.formBuilder.group({
      clientFacilityId: [""],
      providerId: [""],
      shiftStatusTypeId: [""],
    });
  }
  //#endregion
}
